import L from "leaflet";
import { useCallback, useEffect, useRef } from "react";
import {
  AstroBaseMapKey,
  AstroCityHit,
  AstroFeatureProperties,
  AstroLine,
  AstroLineLayerVisibility,
} from "../../../../models/interfaces";
import {
  BASE_MAPS,
  FALLBACK_LINE_COLOR,
  LABEL_HEIGHT,
  LINE_CASING_COLOR,
  LINE_CASING_EXTRA_WEIGHT,
  LINE_CASING_FOCUS_OPACITY,
  LINE_CASING_REST_OPACITY,
  LINE_DIMMED_OPACITY,
  LINE_REST_OPACITY_SCALE,
  MAP_DEFAULT_ZOOM,
  MAP_FALLBACK_CENTER,
  MAP_MAX_BOUNDS,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  WORLD_COPY_RANGE,
  WORLD_WIDTH_DEG,
} from "../utils/astrocartographyConstants";
import {
  getLabelWidth,
  getLineStyle,
  getWorldIndex,
  getZoomWeightScale,
  shiftCoords,
} from "../utils/astrocartographyUtils";

/** Custom panes keep every casing beneath every line, whatever the draw order. */
/** Shared dark popup shell — line, city and birth-place popups all use it. */
const POPUP_CLASS = "astro-popup-shell";

const PANES = {
  casing: { name: "astroCasing", zIndex: 405 },
  lines: { name: "astroLines", zIndex: 410 },
  hit: { name: "astroHit", zIndex: 415 },
  labels: { name: "astroLabels", zIndex: 640 },
} as const;

/** One copy of a line — the same geometry shifted into a neighbouring world. */
interface LineCopy {
  casing?: L.Polyline;
  visible: L.Polyline;
  hit: L.Polyline;
}

interface RenderedLine {
  line: AstroLine;
  group: L.FeatureGroup;
  copies: LineCopy[];
  style: ReturnType<typeof getLineStyle>;
}

interface UseAstrocartographyMapProps {
  lines: AstroLine[];
  planetColors: Record<string, string>;
  birthPlace?: { lat: number; lon: number };
  baseMapKey: AstroBaseMapKey;
  planetVisibility: Record<string, boolean>;
  layerVisibility: AstroLineLayerVisibility;
  showLabels: boolean;
  /** Re-rendered into the open popup whenever the UI language changes. */
  language: string;
  /** Popup body for a line — owned by React so it stays translated and themed. */
  renderPopup: (properties: AstroFeatureProperties) => HTMLElement;
  /** Tooltip/pin text for the birth place marker. */
  birthPlaceLabel: string;
  /** Popup body for a city marker in the search results. */
  renderCityPopup: (city: AstroCityHit, planet: string) => HTMLElement;
  /** Clicking a main line drives the "cities crossed by a line" search. */
  onMainLineClick: (planet: string, angle: string) => void;
}

const WORLD_OFFSETS: number[] = [];
for (let index = -WORLD_COPY_RANGE; index <= WORLD_COPY_RANGE; index += 1) {
  WORLD_OFFSETS.push(index * WORLD_WIDTH_DEG);
}

/**
 * Owns the Leaflet map: creation, basemap swapping, line/label/marker layers and
 * teardown. React state stays the single source of truth for what is visible;
 * this hook only projects that state onto the map.
 *
 * Geometry is drawn once per visible copy of the world and re-centred as the
 * user pans, which is what makes the map scroll endlessly — the payload itself
 * only covers -180…180, so a single copy would leave an empty Earth either side.
 */
const useAstrocartographyMap = ({
  lines,
  planetColors,
  birthPlace,
  baseMapKey,
  planetVisibility,
  layerVisibility,
  showLabels,
  language,
  renderPopup,
  birthPlaceLabel,
  renderCityPopup,
  onMainLineClick,
}: UseAstrocartographyMapProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const baseLayerRef = useRef<L.TileLayer | null>(null);
  const renderedLinesRef = useRef<RenderedLine[]>([]);
  const labelLayerRef = useRef<L.LayerGroup | null>(null);
  const cityMarkersRef = useRef<L.LayerGroup | null>(null);
  const highlightRef = useRef<L.LayerGroup | null>(null);
  const birthLayerRef = useRef<L.LayerGroup | null>(null);
  const openLineRef = useRef<RenderedLine | null>(null);
  const hoveredLineRef = useRef<RenderedLine | null>(null);
  /** Which copy of the world the geometry is currently centred on. */
  const worldIndexRef = useRef<number>(0);
  /** The standing city search, replayed whenever the world copies move. */
  const citySearchRef = useRef<{
    hits: AstroCityHit[];
    planet: string;
    segments: AstroLine[];
  } | null>(null);
  const showLabelsRef = useRef(showLabels);
  showLabelsRef.current = showLabels;

  // Callbacks are read through refs so rebuilding layers is never triggered by
  // a new function identity on re-render.
  const renderPopupRef = useRef(renderPopup);
  const renderCityPopupRef = useRef(renderCityPopup);
  const onMainLineClickRef = useRef(onMainLineClick);
  const planetColorsRef = useRef(planetColors);
  const planetVisibilityRef = useRef(planetVisibility);
  const layerVisibilityRef = useRef(layerVisibility);
  renderPopupRef.current = renderPopup;
  renderCityPopupRef.current = renderCityPopup;
  onMainLineClickRef.current = onMainLineClick;
  planetColorsRef.current = planetColors;
  planetVisibilityRef.current = planetVisibility;
  layerVisibilityRef.current = layerVisibility;

  /* ------------------------------ line styling ---------------------------- */

  const isLineVisible = useCallback(
    (line: AstroLine) =>
      planetVisibilityRef.current[line.planet] !== false &&
      layerVisibilityRef.current[line.category],
    [],
  );

  /** Repaints every line for the current zoom, hover focus and open popup. */
  const applyLineStyles = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const scale = getZoomWeightScale(map.getZoom());
    const focused = hoveredLineRef.current ?? openLineRef.current;

    renderedLinesRef.current.forEach((rendered) => {
      const { style, copies } = rendered;
      const isFocused = focused === rendered;
      // While one line is focused the rest fade back, so the highlighted route
      // reads clearly through the tangle.
      const isDimmed = Boolean(focused) && !isFocused;
      const weight = style.weight * scale + (isFocused ? 2 : 0);

      // Full stroke opacity is the focused state. At rest every line steps
      // back, otherwise all of them together read as one dark mass.
      let opacity = style.opacity * LINE_REST_OPACITY_SCALE;
      if (isFocused) opacity = style.opacity;
      if (isDimmed) opacity = LINE_DIMMED_OPACITY;

      let casingOpacity = LINE_CASING_REST_OPACITY;
      if (isFocused) casingOpacity = LINE_CASING_FOCUS_OPACITY;
      if (isDimmed) casingOpacity = LINE_DIMMED_OPACITY * 0.5;

      copies.forEach((copy) => {
        copy.visible.setStyle({ weight, opacity });
        copy.casing?.setStyle({
          weight: weight + LINE_CASING_EXTRA_WEIGHT,
          opacity: casingOpacity,
        });
      });
    });
  }, []);

  /* ------------------------------- line labels ---------------------------- */

  /**
   * Places each visible main line's name where the line crosses the middle of
   * the viewport, greedily skipping any that would overlap one already placed.
   * Labels follow the map instead of sitting at the poles, where nobody looks.
   */
  const rebuildLabels = useCallback(() => {
    const map = mapRef.current;
    const labelLayer = labelLayerRef.current;
    if (!map || !labelLayer) return;

    labelLayer.clearLayers();
    if (!showLabelsRef.current) return;

    const bounds = map.getBounds();
    const centre = map.getCenter();
    const placed: { x1: number; y1: number; x2: number; y2: number }[] = [];

    // Anchors are chosen with plain arithmetic in lat/lng and only the winning
    // point of each line is projected — projecting every vertex of every copy
    // made panning stutter.
    const candidates = renderedLinesRef.current
      .filter(({ line }) => line.category === "main" && isLineVisible(line))
      .map((rendered) => {
        let anchor: L.LatLng | null = null;
        let bestDistance = Infinity;

        rendered.copies.forEach((copy) => {
          (copy.visible.getLatLngs() as L.LatLng[]).forEach((latLng) => {
            if (!bounds.contains(latLng)) return;
            const dLat = latLng.lat - centre.lat;
            const dLng = latLng.lng - centre.lng;
            const distance = dLat * dLat + dLng * dLng;
            if (distance < bestDistance) {
              bestDistance = distance;
              anchor = latLng;
            }
          });
        });

        return anchor ? { rendered, anchor, distance: bestDistance } : null;
      })
      .filter(Boolean) as {
      rendered: RenderedLine;
      anchor: L.LatLng;
      distance: number;
    }[];

    candidates.sort((a, b) => a.distance - b.distance);

    candidates.forEach(({ rendered, anchor }) => {
      const { line } = rendered;
      const point = map.latLngToContainerPoint(anchor);
      const width = getLabelWidth(line.label);
      const box = {
        x1: point.x - width / 2,
        y1: point.y - LABEL_HEIGHT / 2,
        x2: point.x + width / 2,
        y2: point.y + LABEL_HEIGHT / 2,
      };
      const collides = placed.some(
        (other) =>
          box.x1 < other.x2 &&
          box.x2 > other.x1 &&
          box.y1 < other.y2 &&
          box.y2 > other.y1,
      );
      if (collides) return;
      placed.push(box);

      const color = planetColorsRef.current[line.planet] ?? FALLBACK_LINE_COLOR;
      const icon = L.divIcon({
        className: "",
        html:
          `<span class="astro-line-label" style="--astro-line-color:${color}">` +
          `<span class="astro-line-label-dot"></span>${line.label}</span>`,
        iconSize: [width, LABEL_HEIGHT],
        iconAnchor: [width / 2, LABEL_HEIGHT / 2],
      });
      L.marker(anchor, {
        icon,
        interactive: false,
        keyboard: false,
        pane: PANES.labels.name,
      }).addTo(labelLayer);
    });
  }, [isLineVisible]);

  /* ------------------------------ world copies ---------------------------- */

  /** Re-centres the drawn copies on the world the viewport is currently over. */
  const drawCityResultsRef = useRef<() => void>(() => {});
  const recentreWorldCopies = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const worldIndex = getWorldIndex(map.getCenter().lng);
    if (worldIndex === worldIndexRef.current) return;
    worldIndexRef.current = worldIndex;

    renderedLinesRef.current.forEach(({ line, copies }) => {
      copies.forEach((copy, copyIndex) => {
        const offset =
          WORLD_OFFSETS[copyIndex] + worldIndex * WORLD_WIDTH_DEG;
        const coords = shiftCoords(line.coords, offset);
        copy.visible.setLatLngs(coords);
        copy.hit.setLatLngs(coords);
        copy.casing?.setLatLngs(coords);
      });
    });
    // City pins and the highlighted route ride along with the lines.
    drawCityResultsRef.current();
  }, []);

  /* ------------------------------ map creation ---------------------------- */

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      worldCopyJump: false,
      minZoom: MAP_MIN_ZOOM,
      // Latitude only — longitude is free so the map wraps endlessly.
      maxBounds: L.latLngBounds(MAP_MAX_BOUNDS),
      maxBoundsViscosity: 1.0,
      // Rehomed below — the floating control dock occupies the top-left corner.
      zoomControl: false,
    }).setView(MAP_FALLBACK_CENTER, MAP_MIN_ZOOM);

    Object.values(PANES).forEach((pane) => {
      map.createPane(pane.name);
      const element = map.getPane(pane.name);
      if (element) element.style.zIndex = String(pane.zIndex);
    });

    L.control.zoom({ position: "bottomright" }).addTo(map);
    map.attributionControl.setPrefix("");
    mapRef.current = map;
    labelLayerRef.current = L.layerGroup().addTo(map);
    highlightRef.current = L.layerGroup().addTo(map);
    cityMarkersRef.current = L.layerGroup().addTo(map);
    birthLayerRef.current = L.layerGroup().addTo(map);

    const handleMoveEnd = () => {
      recentreWorldCopies();
      rebuildLabels();
    };
    const handleZoomEnd = () => {
      applyLineStyles();
      rebuildLabels();
    };
    map.on("moveend", handleMoveEnd);
    map.on("zoomend", handleZoomEnd);

    // The container is sized by flexbox, so measure once the layout settles.
    const invalidate = () => map.invalidateSize();
    const frame = window.requestAnimationFrame(invalidate);
    window.addEventListener("resize", invalidate);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", invalidate);
      map.off("moveend", handleMoveEnd);
      map.off("zoomend", handleZoomEnd);
      map.remove();
      mapRef.current = null;
      baseLayerRef.current = null;
      labelLayerRef.current = null;
      highlightRef.current = null;
      cityMarkersRef.current = null;
      birthLayerRef.current = null;
      renderedLinesRef.current = [];
      openLineRef.current = null;
      hoveredLineRef.current = null;
    };
  }, [applyLineStyles, rebuildLabels, recentreWorldCopies]);

  /* -------------------------------- basemap ------------------------------- */

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const definition =
      BASE_MAPS.find((base) => base.key === baseMapKey) ?? BASE_MAPS[0];
    const layer = L.tileLayer(definition.url, {
      maxZoom: MAP_MAX_ZOOM,
      // Providers that stop short of MAP_MAX_ZOOM get their deepest tiles
      // upscaled rather than going blank.
      maxNativeZoom: definition.maxNativeZoom ?? MAP_MAX_ZOOM,
      // Tiles repeat, so the world scrolls continuously in both directions.
      noWrap: false,
      subdomains: definition.subdomains ?? "abc",
      attribution: definition.attribution,
    });

    // Add the replacement before removing the old one to avoid a blank flash.
    layer.addTo(map);
    if (baseLayerRef.current) map.removeLayer(baseLayerRef.current);
    baseLayerRef.current = layer;
    layer.bringToBack();
  }, [baseMapKey]);

  /* ----------------------------- birth marker ----------------------------- */

  useEffect(() => {
    const map = mapRef.current;
    const birthLayer = birthLayerRef.current;
    if (!map || !birthLayer) return;

    birthLayer.clearLayers();
    if (!birthPlace) return;

    const icon = L.divIcon({
      className: "",
      html:
        '<span class="astro-birth-pin"><span class="astro-birth-pulse"></span>' +
        "<span class=\"astro-birth-star\">&#9733;</span></span>",
      iconSize: [26, 26],
      iconAnchor: [13, 13],
    });
    WORLD_OFFSETS.forEach((offset) => {
      L.marker([birthPlace.lat, birthPlace.lon + offset], { icon })
        .addTo(birthLayer)
        .bindPopup(`<div class="astro-popup"><div class="astro-popup-heading">${birthPlaceLabel}</div></div>`, {
          className: POPUP_CLASS,
          maxWidth: 240,
        });
    });
    map.setView([birthPlace.lat, birthPlace.lon], MAP_DEFAULT_ZOOM);
  }, [birthPlace, birthPlaceLabel]);

  /* ------------------------------ line layers ----------------------------- */

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    renderedLinesRef.current.forEach(({ group }) => {
      group.off();
      map.removeLayer(group);
    });
    renderedLinesRef.current = [];
    openLineRef.current = null;
    hoveredLineRef.current = null;
    labelLayerRef.current?.clearLayers();
    worldIndexRef.current = 0;

    // One canvas per pane: thousands of paths stay smooth, and the panes keep
    // casings, lines and hit areas in a fixed stacking order.
    const casingRenderer = L.canvas({ pane: PANES.casing.name });
    const lineRenderer = L.canvas({ pane: PANES.lines.name });
    const hitRenderer = L.canvas({ pane: PANES.hit.name });

    renderedLinesRef.current = lines.map((line) => {
      const style = getLineStyle(line);
      const isMain = line.category === "main";
      const copies: LineCopy[] = [];
      const layers: L.Polyline[] = [];

      WORLD_OFFSETS.forEach((offset) => {
        const coords = shiftCoords(line.coords, offset);
        // Main lines get a dark casing so they stay legible over any basemap.
        const casing = isMain
          ? L.polyline(coords, {
              renderer: casingRenderer,
              pane: PANES.casing.name,
              color: LINE_CASING_COLOR,
              weight: style.weight + LINE_CASING_EXTRA_WEIGHT,
              opacity: LINE_CASING_REST_OPACITY,
              lineCap: "round",
              interactive: false,
            })
          : undefined;
        const visible = L.polyline(coords, {
          ...style,
          renderer: lineRenderer,
          pane: PANES.lines.name,
          lineCap: "round",
          interactive: false,
        });
        // Wide transparent twin so thin lines stay easy to click and tap.
        const hit = L.polyline(coords, {
          renderer: hitRenderer,
          pane: PANES.hit.name,
          color: "#000000",
          opacity: 0,
          weight: 18,
          lineCap: "round",
          interactive: true,
        });

        copies.push({ casing, visible, hit });
        if (casing) layers.push(casing);
        layers.push(visible, hit);
      });

      const group = L.featureGroup(layers);
      const rendered: RenderedLine = { line, group, copies, style };

      group.bindTooltip(line.label, {
        sticky: true,
        className: "astro-line-tooltip",
      });
      group.bindPopup(() => renderPopupRef.current(line.properties), {
        maxWidth: 320,
        className: POPUP_CLASS,
      });
      group.on("popupopen", () => {
        openLineRef.current = rendered;
        applyLineStyles();
      });
      group.on("popupclose", () => {
        if (openLineRef.current === rendered) openLineRef.current = null;
        applyLineStyles();
      });
      group.on("mouseover", () => {
        hoveredLineRef.current = rendered;
        applyLineStyles();
      });
      group.on("mouseout", () => {
        if (hoveredLineRef.current === rendered) hoveredLineRef.current = null;
        applyLineStyles();
      });
      group.on("click", () => {
        if (line.category === "main") {
          onMainLineClickRef.current(line.planet, line.kind);
        }
      });

      return rendered;
    });

    recentreWorldCopies();
    applyLineStyles();
  }, [lines, applyLineStyles, recentreWorldCopies]);

  /* ------------------------------- visibility ----------------------------- */

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    renderedLinesRef.current.forEach(({ line, group }) => {
      if (isLineVisible(line)) {
        if (!map.hasLayer(group)) group.addTo(map);
      } else if (map.hasLayer(group)) {
        map.removeLayer(group);
      }
    });
    applyLineStyles();
    rebuildLabels();
  }, [
    lines,
    planetVisibility,
    layerVisibility,
    isLineVisible,
    applyLineStyles,
    rebuildLabels,
  ]);

  /* --------------------------------- labels ------------------------------- */

  useEffect(() => {
    rebuildLabels();
  }, [lines, showLabels, planetColors, rebuildLabels]);

  /* -------------------- live popup refresh on language swap --------------- */

  useEffect(() => {
    const open = openLineRef.current;
    if (!open) return;
    open.group.setPopupContent(renderPopupRef.current(open.line.properties));
  }, [language]);

  /* ---------------------------- city search layer ------------------------- */

  /** Draws the standing search into whichever worlds are currently on screen. */
  const drawCityResults = useCallback(() => {
    const cityMarkers = cityMarkersRef.current;
    const highlight = highlightRef.current;
    if (!cityMarkers || !highlight) return;

    cityMarkers.clearLayers();
    highlight.clearLayers();

    const search = citySearchRef.current;
    if (!search) return;

    const { hits, planet, segments } = search;
    const color = planetColorsRef.current[planet] ?? FALLBACK_LINE_COLOR;
    const worldOffset = worldIndexRef.current * WORLD_WIDTH_DEG;

    WORLD_OFFSETS.forEach((offset) => {
      const total = offset + worldOffset;
      segments.forEach((segment) => {
        L.polyline(shiftCoords(segment.coords, total), {
          color,
          weight: 9,
          opacity: 0.28,
          lineCap: "round",
          interactive: false,
        }).addTo(highlight);
      });

      hits.forEach((hit, index) => {
        const icon = L.divIcon({
          className: "",
          html:
            `<span class="astro-city-pin" style="--astro-city-color:${color}">` +
            `${index + 1}</span>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });
        L.marker([hit.latitude, hit.longitude + total], { icon })
          .addTo(cityMarkers)
          .bindPopup(() => renderCityPopupRef.current(hit, planet), {
            className: POPUP_CLASS,
            maxWidth: 260,
          });
      });
    });
  }, []);

  drawCityResultsRef.current = drawCityResults;

  const showCityResults = useCallback(
    (hits: AstroCityHit[], planet: string, segments: AstroLine[]) => {
      const map = mapRef.current;
      if (!map) return;

      // Held so the markers can be redrawn when the world copies move.
      citySearchRef.current = { hits, planet, segments };
      drawCityResults();

      if (hits.length) {
        const worldOffset = worldIndexRef.current * WORLD_WIDTH_DEG;
        const bounds = L.latLngBounds(
          hits.map(
            (hit) =>
              [hit.latitude, hit.longitude + worldOffset] as [number, number],
          ),
        );
        map.fitBounds(bounds.pad(0.3));
      }
    },
    [drawCityResults],
  );

  const clearCityResults = useCallback(() => {
    citySearchRef.current = null;
    cityMarkersRef.current?.clearLayers();
    highlightRef.current?.clearLayers();
  }, []);

  /** Re-measure after the control panel opens, closes or resizes. */
  const invalidateSize = useCallback(() => {
    mapRef.current?.invalidateSize();
    rebuildLabels();
  }, [rebuildLabels]);

  return { containerRef, showCityResults, clearCityResults, invalidateSize };
};

export default useAstrocartographyMap;
